import { BTText } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";

import { PercentageDisplay } from "commonComponents/financial/PercentageDisplay/PercentageDisplay";
import {
    type IPercentInvoicedBreakdown,
    PercentInvoicedBreakdown,
} from "commonComponents/financial/PercentInvoicedBreakdown/PercentInvoicedBreakdown";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import {
    GridColumnType,
    type IClientGridColumn,
} from "commonComponents/utilities/Grid/GridContainer.types";

import "./columnPercentInvoiced.less";

export interface IPercentInvoicedResponse {
    hasRetainage: boolean;
    scale: number;
    clientPrice: number;
    invoicedAmount: number;
    afterRetainagePercent: number;
    beforeRetainagePercent: number;
    retainageHeld: number;
    workToComplete: number;
}

const toBreakdown = (cellValue: IPercentInvoicedResponse): IPercentInvoicedBreakdown => {
    const { clientPrice, retainageHeld, workToComplete } = cellValue;
    const percentOfClientPrice = (amount: number) =>
        clientPrice === 0 ? 0 : (amount / clientPrice) * 100;

    return {
        invoicedAmount: cellValue.invoicedAmount,
        afterRetainagePercent: cellValue.afterRetainagePercent,
        retainageHeld,
        retainageHeldPercent: percentOfClientPrice(retainageHeld),
        workToComplete,
        workToCompletePercent: percentOfClientPrice(workToComplete),
        clientPrice,
        showPercentColors: false,
    };
};
function PercentInvoicedCell({
    cellValue,
    field,
}: IGridCellRenderProps<IPercentInvoicedResponse | undefined>) {
    if (!cellValue) {
        return null;
    }

    if (!cellValue.hasRetainage) {
        return (
            <PercentageDisplay
                value={cellValue.afterRetainagePercent}
                decimalScale={cellValue.scale}
                data-testid={field.full}
            />
        );
    }

    return (
        <BTPopover
            content={<PercentInvoicedBreakdown breakdown={toBreakdown(cellValue)} />}
            widthType="unrestricted"
        >
            <div className="percentInvoicedCell" data-testid={field.full}>
                <PercentageDisplay
                    value={cellValue.afterRetainagePercent}
                    decimalScale={cellValue.scale}
                    className="boldTitle"
                    data-testid={`${field.full}_afterRetainage`}
                />
                <BTText
                    size="normal-sm"
                    className="percentInvoicedBeforeRetainage"
                    testid={`${field.full}_beforeRetainage`}
                    text={
                        <>
                            <PercentageDisplay
                                value={cellValue.beforeRetainagePercent}
                                decimalScale={cellValue.scale}
                                data-testid={`${field.full}_beforeRetainageValue`}
                            />{" "}
                            before retainage
                        </>
                    }
                />
            </div>
        </BTPopover>
    );
}

export const columnPercentInvoiced: IClientGridColumn<IPercentInvoicedResponse | undefined> = {
    columnType: GridColumnType.percentInvoiced,
    Cell: PercentInvoicedCell,
};
